import {
  resetPassword,
  signInWithEmail,
  signInWithGoogle,
  signInWithLinkedIn,
  signOut as signOutApi,
  signUpWithEmail,
} from "@/lib/auth";
import { useStore } from "@/store";
import { useCallback } from "react";

/**
 * Custom hook to wrap authentication logic and ensure proper cleanup on sign out.
 */
export const useAuth = () => {
  // Zustand store state and actions
  const unsubscribeDomains = useStore((state) => state.unsubscribeDomains);
  const unsubscribePages = useStore((state) => state.unsubscribePages);
  const unsubscribeJobs = useStore((state) => state.unsubscribeJobs);
  const unsubscribePersonas = useStore((state) => state.unsubscribePersonas);
  const unsubscribeOffers = useStore((state) => state.unsubscribeOffers);
  const unsubscribePolicies = useStore((state) => state.unsubscribePolicies);

  // Auth actions
  const signUp = useCallback(
    (email: string, password: string, firstName: string, lastName: string) =>
      signUpWithEmail(email, password, firstName, lastName),
    [],
  );

  const signIn = useCallback(
    (email: string, password: string) => signInWithEmail(email, password),
    [],
  );

  const signInGoogle = useCallback(() => signInWithGoogle(), []);

  const signInLinkedIn = useCallback(() => signInWithLinkedIn(), []);

  const resetUserPassword = useCallback(
    (email: string) => resetPassword(email),
    [],
  );

  // Enhanced signOut: also unsubscribes from all data listeners
  const signOut = useCallback(async () => {
    // Unsubscribe from all listeners before signing out
    unsubscribeDomains();
    unsubscribePages();
    unsubscribeJobs();
    unsubscribePersonas();
    unsubscribeOffers();
    unsubscribePolicies();
    await signOutApi();
  }, [
    unsubscribeDomains,
    unsubscribePages,
    unsubscribeJobs,
    unsubscribePersonas,
    unsubscribeOffers,
    unsubscribePolicies,
  ]);

  return {
    signUp,
    signIn,
    signInGoogle,
    signInLinkedIn,
    resetUserPassword,
    signOut,
  };
};
